import trendArrow from "@assets/trend_arrow.gif";
import ArticleCard from "@blocks/Card/ArticleCard";
import Divider from "@elements/Divider/Divider";
import Heading from "@elements/Typography/Heading";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { isArrayWithElements } from "@helper/utils";
import useSwrApi from "@hooks/useSwrApi";
import { twMerge } from "tailwind-merge";

type MostReadProps = {
  /**
   * List of articles to display in the Most Read section.
   * When not provided, the articles are fetched from the `most-read` API.
   */
  mostRead?: ProcessedArticleData[];

  /**
   * Checks whether the article first-fold design is enabled.
   * @default false
   */
  isArticleFirstFoldEnabled?: boolean;
};

/**
 * Displays the Most Read articles section.
 *
 * Uses the provided articles when available; otherwise, it fetches the
 * Most Read articles from the API. The section displays up to five articles.
 *
 * @param props - Props used to configure the Most Read section.
 * @returns The Most Read articles section.
 */
export default function MostRead({
  mostRead,
  isArticleFirstFoldEnabled = false,
}: MostReadProps) {
  let mostReadData = mostRead;
  if (!mostRead) {
    const { data: mostReadApiData } =
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useSwrApi<ProcessedArticleData[]>("most-read");
    mostReadData = mostReadApiData;
  }
  const source = "most_read";
  return (
    <div className="w-full min-h-[400px] mt-5">
      {isArrayWithElements(mostReadData) ? (
        <>
          <div className="w-full bg-red-400 pt-3 px-3">
            <Heading
              Element="h2"
              className={twMerge(
                "text-gray-100 text-md font-bold inline-flex",
                isArticleFirstFoldEnabled && "text-sm leading-200",
              )}
            >
              <img
                src={trendArrow}
                alt={"trend-arrow"}
                width={28}
                height={28}
                className="mr-1"
              />
              அதிகம் படித்தவை
            </Heading>
            {mostReadData.slice(0, 5).map((article, i, row) => (
              <ArticleCard
                displayWidth={135}
                displayHeight={90}
                key={article.id}
                elements={{ hasParagraph: false, hasDate: true }}
                overrideStyle={{
                  cardWrapper: twMerge(
                    i + 1 === row.length
                      ? "flex-row border-b-0"
                      : "flex-row border-b-gray-2050",
                    isArticleFirstFoldEnabled && "border-b-0",
                  ),
                  titleStyle:
                    "font-semibold font-primary text-2sm text-gray-100 hover:text-gray-100",
                  imageWrapperStyle: twMerge(
                    "h-23",
                    isArticleFirstFoldEnabled
                      ? "lg:w-auto"
                      : "w-31.5 max-w-40 lg:w-31.5 sm:w-full",
                  ),
                  contentWrapper: "gap-xs",
                  titleWrapper: "gap-0",
                  imageStyle: twMerge(
                    "h-23",
                    isArticleFirstFoldEnabled ? "w-31.5" : "w-full",
                  ),
                }}
                refMedium={source}
                {...article}
              />
            ))}
          </div>
          <Divider
            className="mt-5 border-b border-gray-2200"
            enableDivider={!isArticleFirstFoldEnabled}
          />
        </>
      ) : null}
    </div>
  );
}
